import {skipToken} from '@reduxjs/toolkit/query';
import {Button} from 'platform/components';
import {
  Box,
  Grid,
  HStack,
  Heading,
  Icon,
  Show,
  Text,
  VStack,
  type IconProps,
} from 'platform/foundation';

import {useGetAuditDataQuery, useGetAuditStructureQuery} from '@dms/api/core';
import i18n from '@dms/i18n';

import {inspectionRatings} from '../../model/inspectionRatings';
import {RatingStars} from '../components/RatingStars';

function ConditionCard(props: {icon: IconProps['value']; label: string; score: number | null}) {
  return (
    <Box padding={5} minHeight={29} borderRadius="medium" backgroundColor="palettes.white.10.100">
      <HStack spacing={4} align="center" justify="center" height="100%">
        <Icon value={props.icon} size={9} color="palettes.neutral.900.100" />
        <Box minWidth={0}>
          <VStack spacing={3}>
            <Heading size={4} alternative>
              {props.label}
            </Heading>
            <Box role="img" aria-label={props.label}>
              <RatingStars rating={props.score} />
            </Box>
            <Show when={props.score === null}>
              <Text size="xSmall" color="tertiary">
                {i18n.t('entity.auction.preview.notRated')}
              </Text>
            </Show>
          </VStack>
        </Box>
      </HStack>
    </Box>
  );
}

// Prototype v11 Fastback vehicle condition certificate.
export function BuyerCertificate(props: {inspectionId: string | null}) {
  const {currentData: audits} = useGetAuditDataQuery(
    props.inspectionId ? {auditId: props.inspectionId} : skipToken
  );
  const audit = audits?.[0];
  const {currentData: structure} = useGetAuditStructureQuery(
    audit ? {auditStructureId: audit.controlStructureId} : skipToken
  );
  const stars = inspectionRatings(structure?.[0], audit);

  return (
    <Box
      padding={6}
      borderRadius="medium"
      backgroundColor="palettes.purple.10.100"
      data-testid="auction-preview-certificate"
    >
      <VStack spacing={6}>
        <HStack spacing={4} align="center" justify="space-between">
          <Heading size={3} alternative>
            {i18n.t('entity.auction.preview.conditionCertificate')}
          </Heading>
          {/* shortcut: the certificate results are not built yet, enable with the condition ratings API. */}
          <Button
            variant="primary"
            isDisabled
            rightIcon="action/open_in_new"
            title={i18n.t('entity.auction.preview.showResults')}
          />
        </HStack>
        <Grid columns={[1, 1, 3]} spacing={5}>
          <ConditionCard
            icon="automotive/damage"
            label={i18n.t('entity.auction.preview.exterior')}
            score={stars.exterior}
          />
          <ConditionCard
            icon="condition/drivers_seat"
            label={i18n.t('entity.auction.preview.interior')}
            score={stars.interior}
          />
          <ConditionCard
            icon="automotive/engine"
            label={i18n.t('entity.auction.preview.technicalCondition')}
            score={stars.technical}
          />
        </Grid>
      </VStack>
    </Box>
  );
}
